import { Root, Spinner } from '@yoast/ui-library';
import { type FC, useEffect, useState } from 'react';
import { MemoryRouter } from 'react-router-dom';

import App from './App';
import RouterSync from './components/RouterSync';
import './index.css';
import {
  setApiBaseUrl,
  setMyYoastAccessTokenFactory,
} from './orval/custom-instance';

export interface AIInsightsClientProps {
  /**
   * Whether the UI should be rendered in RTL mode. Defaults to false.
   */
  isRtl?: boolean;

  /**
   * Optional CSS class name to apply to the root container.
   */
  className?: string;

  /**
   * Optional inline styles to apply to the root container.
   */
  style?: React.CSSProperties;

  /**
   * Authentication token to use for calls to the API.
   */
  token: string | (() => string);

  /**
   * Optional API URL to use for calls to the backend.
   */
  apiUrl?: string;

  /**
   * The environment in which the client is running.
   *
   * This can be used to enable or disable certain features or logging
   * based on whether the client is running in development, staging, or
   * production environments.
   *
   * If not defined by the host application, it will be determined based
   * on hostname.
   *
   * @example
   * // Enable debug logging in development
   * if (env === 'development') {
   *   console.log('Debug info...');
   * }
   */
  env?: 'development' | 'staging' | 'production';

  /**
   * Optional debug info component to display in the sidebar. Typically
   * only shown while in development/local env.
   */
  DebugInfo?: React.FC;

  /**
   * The base router path to use for the application.
   *
   * This is useful when the AI Insights Client is embedded within a larger
   * application that has its own routing structure. The basename allows you
   * to mount the client at a specific route prefix.
   *
   * @example
   * // Mount the client at "/dashboard/insights"
   * <AIInsightsClient basename="/dashboard/insights" />
   *
   * @default "/"
   */
  basename?: string;

  /**
   * The current route within the application.
   *
   * This represents the specific page/route within the embedded client
   * that should be initially displayed.
   *
   * @example
   * // Start at the setup page
   * <AIInsightsClient subpath="/setup" />
   *
   * @default "/"
   */
  subpath?: string;

  /**
   * Callback function invoked when the internal router navigates to a new route.
   *
   * This allows the parent application to stay in sync with the client's
   * internal navigation state. Useful for updating the browser URL or
   * handling navigation events in the embedding application.
   *
   * @param to - The full path (including pathname, search, and hash) that
   *             the client navigated to
   *
   * @example
   * // Keep parent router in sync
   * <AIInsightsClient
   *   onNavigate={(path) => {
   *     parentRouter.navigate(path);
   *   }}
   * />
   *
   * @example
   * // Track navigation for analytics
   * <AIInsightsClient
   *   onNavigate={(path) => {
   *     analytics.track('ai_insights_navigation', { path });
   *   }}
   * />
   */
  onNavigate?: (to: string) => void;
  /**
   * Callback function invoked when the user requests to log out.
   *
   * This allows the parent application to handle logout actions initiated
   * from within the AI Insights Client, such as clearing authentication
   * state or redirecting to a login page.
   *
   * @example
   * // Handle logout in parent application
   * <AIInsightsClient
   *   onLogout={() => {
   *     auth.logout();
   *     parentRouter.navigate('/login');
   *   }}
   * />
   */
  onLogout?: () => void;
  /**
   * Callback function invoked when the user requests to add a subscription.
   *
   * This allows the parent application to handle subscription upgrade actions
   * initiated from within the AI Insights Client, such as opening a subscription
   * modal or redirecting to a billing page.
   *
   * @example
   * // Handle subscription upgrade in parent application
   * <AIInsightsClient
   *   onAddSubscription={() => {
   *     billing.openSubscriptionModal();
   *   }}
   * />
   */
  onAddSubscription?: () => void;
}

/**
 * AIInsightsClient - A React component for brand analysis and AI insights.
 *
 * This component can be embedded in other React applications and provides
 * a complete brand analysis interface with setup wizard, analysis results,
 * and settings management.
 */
export const AIInsightsClient: FC<AIInsightsClientProps> = ({
  isRtl = false,
  className,
  style,
  token = '',
  apiUrl,
  env,
  DebugInfo,
  basename = '/',
  subpath = '/',
  onNavigate = () => {},
  onLogout = () => {},
  onAddSubscription = () => {},
}) => {
  // Helper function to safely combine paths
  const combinePaths = (base: string, route: string): string => {
    const normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
    const normalizedRoute = route.startsWith('/') ? route : `/${route}`;
    return normalizedBase + normalizedRoute || '/';
  };
  const [configurationStillApplying, setConfigurationStillApplying] = useState(
    Boolean(token || apiUrl),
  );

  // Collect lib props to share with context
  const hostAppProps = {
    onAddSubscription,
    onLogout,
    onNavigate,
    env,
    DebugInfo,
    basename,
    subpath,
  };

  // Configure API base URL if provided
  useEffect(() => {
    if (apiUrl) {
      // Set the API Base URL for the axios instance that orval's generated client uses.
      setApiBaseUrl(apiUrl);
      if (import.meta.env.DEV) {
        console.log('[AIInsightsClient] API base URL configured:', apiUrl);
      }
    }
    // Configure token (factory) to inject a custom MyYoast access token source.
    setMyYoastAccessTokenFactory(
      typeof token !== 'function' ? () => token : token,
    );
    if (import.meta.env.DEV) {
      console.log('[AIInsightsClient] token source configured');
    }
    setConfigurationStillApplying(false);
  }, [apiUrl, token]);

  const wrapperClassName = ['ai-insights-app', className]
    .filter(Boolean)
    .join(' ');

  if (configurationStillApplying) {
    return (
      <div className={wrapperClassName} style={style}>
        <Spinner />
      </div>
    );
  }

  return (
    <div className={wrapperClassName} style={style}>
      <MemoryRouter
        initialEntries={[combinePaths(basename, subpath)]}
        basename={basename || '/'}
      >
        <RouterSync subpath={subpath} onNavigate={onNavigate} />
        <Root context={{ isRtl }}>
          <App DebugInfo={DebugInfo} hostAppProps={hostAppProps} />
        </Root>
      </MemoryRouter>
    </div>
  );
};

// Export the main component as default
export default AIInsightsClient;
